import type { Primitive } from './types';

/** * Define a formatter that can take in a generic object type and format each entry of that object */
export type FormatterSchema<T> = {
  [K in keyof T]?: T[K] extends Primitive
    ? (val: T[K]) => T[K]
    : FormatterSchema<T[K]> | ((val: T[K]) => T[K]);
};

type Formatter<T> = (val: T) => T;
const isFormatterFn = <T>(
  formatter: FormatterSchema<T>[keyof T],
): formatter is Formatter<T[keyof T]> => typeof formatter === 'function';

const isFormatterObj = <T>(
  formatter: FormatterSchema<T>[keyof T],
): formatter is Exclude<Formatter<T[keyof T]>, Function> =>
  typeof formatter === 'object';

export const createFormatter =
  <T>(schema: FormatterSchema<T>): Formatter<T> =>
  (val) => {
    const keys = Object.keys(schema) as Array<keyof T>;
    return keys.reduce<T>((prev, key) => {
      const keySchema = schema[key];
      const keyVal = val[key];

      const isSchemaFn = isFormatterFn(keySchema);
      if (isSchemaFn) {
        return {
          ...prev,
          [key]: keySchema(keyVal),
        };
      }

      const isSchemaObj = isFormatterObj(keySchema);
      if (isSchemaObj) {
        return {
          ...prev,
          [key]: createFormatter(keySchema)(keyVal),
        };
      }

      return prev;
    }, val);
  };
